import {
  Box,
  Checkbox,
  Drawer,
  FormControlLabel,
  Skeleton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import classNames from "classnames";
import { Icons } from "assets";
import React, { useEffect, useState } from "react";
import {
  getAllAgeRangesService,
  getAllLanguagesService
} from "shared/services/general";
import { toastMessage } from "shared/components/toast";
import styles from "./style.module.scss";

export type BookFilterValues = { ageRangeIds: number[]; languageIds: number[] };

export const emptyBookFilters: BookFilterValues = {
  ageRangeIds: [],
  languageIds: []
};

const ageRangeLabel = (ageRange: any) =>
  String(ageRange?.text ?? "").replace(/^Age:\s*/i, "");

interface BookFiltersProps {
  appliedFilters: BookFilterValues;
  onApply: (filters: BookFilterValues) => void;
}

/**
 * Filters button, applied-filter chips and the age range / language drawer.
 * The parent keeps the applied filters and sends them with its book request.
 */
export default function BookFilters({
  appliedFilters,
  onApply
}: BookFiltersProps) {
  const [ageRanges, setAgeRanges] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [draftFilters, setDraftFilters] =
    useState<BookFilterValues>(emptyBookFilters);

  const isActive =
    appliedFilters.ageRangeIds.length + appliedFilters.languageIds.length > 0;
  const hasDraftFilters =
    draftFilters.ageRangeIds.length + draftFilters.languageIds.length > 0;

  useEffect(() => {
    Promise.all([getAllAgeRangesService(), getAllLanguagesService()])
      .then(([ageRangeResponse, languageResponse]) => {
        const isActiveOption = (item: any) => item?.is_active !== 0;
        setAgeRanges((ageRangeResponse?.data?.data ?? []).filter(isActiveOption));
        setLanguages((languageResponse?.data?.data ?? []).filter(isActiveOption));
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
      })
      .finally(() => setOptionsLoading(false));
  }, []);

  const openDrawer = () => {
    setDraftFilters(appliedFilters);
    setOpen(true);
  };

  const toggleDraftFilter = (key: keyof BookFilterValues, id: number) => {
    setDraftFilters((previous) => ({
      ...previous,
      [key]: previous[key].includes(id)
        ? previous[key].filter((selectedId) => selectedId !== id)
        : [...previous[key], id]
    }));
  };

  const applyFilters = () => {
    onApply(draftFilters);
    setOpen(false);
  };

  const removeAppliedFilter = (key: keyof BookFilterValues, id: number) => {
    onApply({
      ...appliedFilters,
      [key]: appliedFilters[key].filter((selectedId) => selectedId !== id)
    });
  };

  const chips = [
    ...appliedFilters.ageRangeIds.map((id) => ({
      key: "ageRangeIds" as const,
      id,
      label: `Age ${ageRangeLabel(ageRanges.find((item) => item?.id === id))}`
    })),
    ...appliedFilters.languageIds.map((id) => ({
      key: "languageIds" as const,
      id,
      label: languages.find((item) => item?.id === id)?.name ?? ""
    }))
  ];

  const renderOptions = (
    options: any[],
    key: keyof BookFilterValues,
    getLabel: (option: any) => string
  ) => {
    if (optionsLoading) {
      return [1, 2, 3, 4].map((line) => (
        <Skeleton
          key={line}
          height={20}
          width="60%"
          className={styles.filterSkeleton}
        />
      ));
    }

    if (options.length === 0) {
      return <div className={styles.filterEmpty}>No options available</div>;
    }

    return options.map((option) => (
      <FormControlLabel
        key={option?.id}
        className={styles.filterOption}
        label={getLabel(option)}
        control={
          <Checkbox
            size="small"
            className={styles.filterCheckbox}
            checked={draftFilters[key].includes(option?.id)}
            onChange={() => toggleDraftFilter(key, option?.id)}
          />
        }
      />
    ));
  };

  return (
    <>
      <div className={styles.filtersToggleWrapper}>
        <button
          className={classNames(styles.filtersToggle, {
            [styles.filtersToggleActive]: isActive
          })}
          onClick={openDrawer}
        >
          <Icons.FilterIcon />
          Filters
        </button>
        {chips.length > 0 && (
          <div className={styles.appliedFilters}>
            {chips.map((chip) => (
              <span
                key={`${chip.key}-${chip.id}`}
                className={styles.appliedFilterChip}
              >
                {chip.label}
                <CloseIcon
                  className={styles.appliedFilterRemove}
                  onClick={() => removeAppliedFilter(chip.key, chip.id)}
                />
              </span>
            ))}
          </div>
        )}
      </div>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box className={styles.filterDrawer}>
          <div className={styles.filterDrawerHeader}>
            <span>Filters</span>
            <CloseIcon
              className={styles.filterDrawerClose}
              onClick={() => setOpen(false)}
            />
          </div>
          <div className={styles.filterDrawerBody}>
            <div className={styles.filterSection}>
              <div className={styles.filterSectionTitle}>Age range</div>
              {renderOptions(ageRanges, "ageRangeIds", ageRangeLabel)}
            </div>
            <div className={styles.filterSection}>
              <div className={styles.filterSectionTitle}>Language</div>
              {renderOptions(
                languages,
                "languageIds",
                (language) => language?.name
              )}
            </div>
          </div>
          <div className={styles.filterDrawerActions}>
            <button
              className={styles.filterReset}
              disabled={!hasDraftFilters}
              onClick={() => setDraftFilters(emptyBookFilters)}
            >
              Reset
            </button>
            <button className={styles.filterApply} onClick={applyFilters}>
              Apply
            </button>
          </div>
        </Box>
      </Drawer>
    </>
  );
}
